import React, { useCallback, useEffect, useState } from 'react';
import { Radar, RefreshCw, Send, CheckCircle2, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KIND_STYLES = {
  article: 'bg-blue-50 text-blue-600',
  product: 'bg-purple-50 text-purple-600',
  bulk: 'bg-amber-50 text-amber-700',
  manual: 'bg-gray-100 text-gray-600',
};

const timeAgo = (iso) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const shortUrl = (u) => (u || '').replace(/^https?:\/\/[^/]+/, '') || '/';

export default function IndexNowActivityCard() {
  const token = sessionStorage.getItem('admin_token');
  const [data, setData] = useState(null);
  const [pushing, setPushing] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    fetch(`${API_URL}/api/admin/indexnow/activity`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then(setData)
      .catch(console.error);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const pushAll = async () => {
    setPushing(true);
    setMsg('');
    try {
      const res = await fetch(`${API_URL}/api/admin/indexnow/submit-all`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      setMsg(d.ok
        ? `Pushed ${d.count} URLs (${d.article_count} articles · ${d.product_count} products · ${d.hub_count} hubs) → HTTP ${d.status}`
        : `Push failed → HTTP ${d.status} ${d.error || ''}`);
      load();
    } catch (err) {
      setMsg(String(err.message || err));
    } finally { setPushing(false); }
  };

  const stats = data?.stats || {};

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="indexnow-activity-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Radar className="w-4 h-4 text-teal-500" /> Indexing Activity (IndexNow)
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="p-1.5 rounded-lg text-gray-400 hover:text-teal-500 transition-colors"
            data-testid="indexnow-refresh-btn"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={pushAll}
            disabled={pushing}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-500 hover:bg-teal-600 text-white text-[11px] font-bold disabled:opacity-50 transition-colors"
            data-testid="indexnow-push-all-btn"
          >
            <Send className="w-3 h-3" /> {pushing ? 'Pushing…' : 'Push All URLs'}
          </button>
        </div>
      </div>
      <p className="text-[11px] text-gray-400 mb-4">
        Every article publish/update and Shopify product change auto-pings Bing, Yandex, DuckDuckGo, Naver &amp; Seznam.
        Google doesn't accept pings — it reads the sitemap's lastmod dates, which update automatically.
      </p>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="rounded-lg bg-gray-50 p-3 text-center" data-testid="indexnow-stat-pings">
          <p className="text-lg font-bold text-gray-900">{stats.pings_24h ?? '—'}</p>
          <p className="text-[10px] text-gray-500 uppercase tracking-wide">Pings · 24h</p>
        </div>
        <div className="rounded-lg bg-gray-50 p-3 text-center" data-testid="indexnow-stat-urls">
          <p className="text-lg font-bold text-gray-900">{stats.urls_24h ?? '—'}</p>
          <p className="text-[10px] text-gray-500 uppercase tracking-wide">URLs pushed · 24h</p>
        </div>
        <div className="rounded-lg bg-gray-50 p-3 text-center" data-testid="indexnow-stat-failed">
          <p className={`text-lg font-bold ${stats.failed_24h ? 'text-red-500' : 'text-gray-900'}`}>{stats.failed_24h ?? '—'}</p>
          <p className="text-[10px] text-gray-500 uppercase tracking-wide">Failed · 24h</p>
        </div>
      </div>

      {msg && <p className="text-[11px] text-teal-700 mb-3" data-testid="indexnow-push-msg">{msg}</p>}

      <div className="max-h-72 overflow-y-auto" data-testid="indexnow-ping-list">
        {(data?.pings || []).length === 0 ? (
          <p className="text-xs text-gray-400 py-4 text-center">No pings yet — publish an article or update a product and it'll appear here.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 text-xs border-b border-gray-100">
                <th className="pb-2 font-medium">When</th>
                <th className="pb-2 font-medium">Type</th>
                <th className="pb-2 font-medium">URL(s)</th>
                <th className="pb-2 font-medium text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {(data?.pings || []).map((p) => (
                <tr key={p.ping_id} className="border-b border-gray-50 last:border-0" data-testid={`indexnow-ping-row-${p.ping_id}`}>
                  <td className="py-2 text-gray-400 text-xs whitespace-nowrap" title={p.pinged_at}>{timeAgo(p.pinged_at)}</td>
                  <td className="py-2">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${KIND_STYLES[p.kind] || KIND_STYLES.manual}`}>{p.kind}</span>
                  </td>
                  <td className="py-2 text-gray-600 text-xs truncate max-w-[220px]" title={(p.urls || []).join('\n')}>
                    {shortUrl(p.urls?.[0])}{p.count > 1 && <span className="text-gray-400"> +{p.count - 1} more</span>}
                  </td>
                  <td className="py-2 text-right">
                    {p.ok ? (
                      <span className="inline-flex items-center gap-1 text-emerald-600 text-[11px] font-semibold"><CheckCircle2 className="w-3 h-3" /> {p.status}</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-500 text-[11px] font-semibold" title={p.error || ''}><XCircle className="w-3 h-3" /> {p.status || 'ERR'}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
